import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EntrepriseServices from "../../services/entreprise/entrepriseServices";
import OffreServices from "../../services/offre/offreServices";
import entrepriseInterface from "../../models/entreprise.interface";
import Loader from "../components/loader";
import "../../assets/css/createOrUpdate.css";

function CreateOrUpdateEntreprise() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({ ...entrepriseInterface });
  const [offres, setOffres] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ================= FETCH DATA =================

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const offresRes = await OffreServices.getAll();
        setOffres(offresRes.data.data || []);

        if (id) {
          const entrepriseRes = await EntrepriseServices.getOne(id);
          if (entrepriseRes.data) {
            setFormData(entrepriseRes.data.data);
          }
        } else {
          setFormData({
            nom: "",
            adresse: "",
            email: "",
            telephone: "",
            nif: "",
            stat: "",
            rcs: "",
            logo: "",
            offre: "",
            dateDebut: "",
            dateExpiration: "",
            heureOuverture: "",
            heureFermeture: "",
            tolerancePauseMinutes: 10,
          });
        }
      } catch (err) {
        console.error("Erreur lors du chargement des données :", err);
        setMessage({
          type: "error",
          text: "Erreur lors du chargement des données.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // ================= AUTO EXPIRATION DATE =================

  useEffect(() => {
    if (formData.dateDebut && !formData._id) {
      const debut = new Date(formData.dateDebut);
      const expiration = new Date(debut);
      expiration.setMonth(expiration.getMonth() + 1);

      setFormData((prev) => ({
        ...prev,
        dateExpiration: expiration.toISOString().substring(0, 10),
      }));
    }
  }, [formData.dateDebut, formData._id]);

  // ================= HELPERS =================

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "logo" && files && files.length > 0) {
      try {
        const base64 = await convertFileToBase64(files[0]);
        setFormData((prev) => ({ ...prev, logo: base64 }));
      } catch (err) {
        console.error("Erreur conversion fichier en base64 :", err);
      }
    } else if (name === "offre") {
      const selectedOffre = offres.find((o) => o._id === value);
      setFormData((prev) => ({ ...prev, offre: selectedOffre || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      if (id) {
        await EntrepriseServices.update(id, formData);
        setMessage({
          type: "success",
          text: "Entreprise mise à jour avec succès !",
        });
      } else {
        await EntrepriseServices.create(formData);
        setMessage({
          type: "success",
          text: "Entreprise créée avec succès !",
        });
      }
      setTimeout(() => navigate("/entreprises"), 1500);
    } catch (err) {
      console.error("Erreur :", err);
      const errMsg = err?.response?.data?.message || "Une erreur est survenue.";
      setError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">
          {id ? "Modifier l'entreprise" : "Créer une entreprise"}
        </h2>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Informations de l'entreprise</h3>
        </div>

        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Nom</label>
                  <input
                    type="text"
                    name="nom"
                    className="form-control"
                    value={formData.nom || ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Adresse</label>
                  <input
                    type="text"
                    name="adresse"
                    className="form-control"
                    value={formData.adresse || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Téléphone</label>
                  <input
                    type="text"
                    name="telephone"
                    className="form-control"
                    value={formData.telephone || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">NIF</label>
                  <input
                    type="text"
                    name="nif"
                    className="form-control"
                    value={formData.nif || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">STAT</label>
                  <input
                    type="text"
                    name="stat"
                    className="form-control"
                    value={formData.stat || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">RCS</label>
                  <input
                    type="text"
                    name="rcs"
                    className="form-control"
                    value={formData.rcs || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Logo</label>
                  <input
                    type="file"
                    name="logo"
                    accept="image/*"
                    className="form-control"
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6 d-flex align-items-end">
                  {formData.logo && (
                    <img
                      src={formData.logo}
                      alt="Logo Preview"
                      className="entreprise-logo-preview"
                    />
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Offre</label>
                  <select
                    name="offre"
                    className="form-select"
                    value={formData?.offre?._id || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Choisir une offre --</option>
                    {offres.map((offre) => (
                      <option key={offre._id} value={offre._id}>
                        {offre.designation}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Date de début</label>
                  <input
                    type="date"
                    name="dateDebut"
                    className="form-control"
                    value={formData.dateDebut?.substring(0, 10) || ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Date d'expiration</label>
                  <input
                    type="date"
                    name="dateExpiration"
                    className="form-control"
                    value={formData.dateExpiration?.substring(0, 10) || ""}
                    onChange={handleChange}
                    required
                    disabled={!formData.dateDebut}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Heure d'ouverture</label>
                  <input
                    type="time"
                    name="heureOuverture"
                    className="form-control"
                    value={formData.heureOuverture || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Heure de fermeture</label>
                  <input
                    type="time"
                    name="heureFermeture"
                    className="form-control"
                    value={formData.heureFermeture || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">
                    Tolérance de pause (minutes)
                  </label>
                  <input
                    type="number"
                    name="tolerancePauseMinutes"
                    className="form-control"
                    value={formData.tolerancePauseMinutes || 10}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("/entreprises")}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-success btn-sm"
                  disabled={saving}
                >
                  {saving ? "Enregistrement..." : id ? "Enregistrer" : "Créer"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateOrUpdateEntreprise;
